/**
 * Paragraph parser — converts <w:p> elements to ProseMirror paragraph/heading nodes.
 *
 * Each <w:p> in OOXML contains:
 *   - <w:pPr> (paragraph properties: style, alignment, numbering, spacing, indent)
 *   - <w:r> elements (runs of text)
 *   - <w:hyperlink> elements (links wrapping runs)
 *   - <w:bookmarkStart/End> (bookmarks — ignored for now)
 *
 * This parser handles:
 * - Basic paragraphs → paragraph nodes
 * - Heading styles → heading nodes (level 1-6)
 * - Text alignment → textAlign attr
 * - Paragraph spacing/indentation → ParagraphStyleExtension attrs
 * - Hyperlinks → link marks on text
 */

import {
  PMNode,
  PMMark,
  ParagraphProperties,
  RunProperties,
  ParseContext,
} from './types';
import {
  getChildNS,
  parseParagraphPropertiesFromElement,
  W_NS,
} from './zipReader';
import { parseRun } from './runParser';
import { detectHeadingLevel, resolveStyleProperties } from './context';
import { CommentTracker } from './commentParser';
import {
  parseInsertion,
  parseDeletion,
  TrackChangeTracker,
} from './trackChangeParser';
import { convertOmmlToLatex } from './mathParser';

const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

/** Find the <w:sdtContent> child inside a <w:sdt> element. */
function findSdtContent(sdtEl: Element): Element | null {
  const children = sdtEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'sdtContent') return el;
  }
  return null;
}

/**
 * Parse a single <w:p> element into a ProseMirror node (paragraph or heading).
 */
export function parseParagraph(pEl: Element, ctx: ParseContext): PMNode {
  // 0. Detect horizontal rules (pBdr bottom border on empty paragraphs)
  const pPrElCheck = getChildNS(pEl, W_NS, 'pPr');
  if (pPrElCheck && isHorizontalRule(pPrElCheck, pEl)) {
    return { type: 'horizontalRule' };
  }

  // 1. Parse paragraph properties
  const pPrEl = pPrElCheck;
  const pProps = pPrEl ? parseParagraphPropertiesFromElement(pPrEl) : {};

  // 2. Resolve style-based properties
  let resolvedPProps = { ...pProps };
  if (pProps.styleId) {
    const styleResolved = resolveStyleProperties(pProps.styleId, ctx);
    // Merge: explicit paragraph properties override style-inherited ones
    resolvedPProps = { ...styleResolved.paragraphProperties, ...pProps };
  }

  // 3. Detect heading level
  const headingLevel = detectHeadingLevel(pProps.styleId, resolvedPProps, ctx);

  // 4. Parse inline content (runs + hyperlinks)
  const content = parseParagraphContent(pEl, ctx);

  // 4b. Strip redundant style-inherited run properties from text nodes.
  // When a run explicitly sets fontSize/color/fontFamily that matches the
  // paragraph style's default, the inline mark would override TipTap's CSS.
  // For headings, we preserve fontSize so the DOCX's explicit size overrides
  // our CSS heading sizes (which don't match Word's defaults).
  if (pProps.styleId) {
    const styleResolved = resolveStyleProperties(pProps.styleId, ctx);
    const styleRunProps = styleResolved.runProperties;
    if (styleRunProps) {
      stripRedundantStyleMarks(content, styleRunProps, !!headingLevel);
    }
  }

  // 5. Build the node
  const nodeType = headingLevel ? 'heading' : 'paragraph';
  const attrs: Record<string, unknown> = {};

  // Node ID
  attrs.id = ctx.generateNodeId();

  // Heading level
  if (headingLevel) {
    attrs.level = headingLevel;
  }

  // Text alignment (map "both" to "justify" for TipTap)
  if (resolvedPProps.alignment) {
    attrs.textAlign =
      resolvedPProps.alignment === 'both'
        ? 'justify'
        : resolvedPProps.alignment;
  }

  // Paragraph style attrs (ParagraphStyleExtension expects "Xpt" strings)
  if (resolvedPProps.marginLeft || resolvedPProps.leftIndent) {
    const val = resolvedPProps.marginLeft || resolvedPProps.leftIndent;
    attrs.marginLeft = `${val}pt`;
  }
  if (resolvedPProps.rightIndent) {
    attrs.marginRight = `${resolvedPProps.rightIndent}pt`;
  }
  if (resolvedPProps.spaceBefore) {
    attrs.marginTop = `${resolvedPProps.spaceBefore}pt`;
  }
  if (resolvedPProps.spaceAfter) {
    attrs.marginBottom = `${resolvedPProps.spaceAfter}pt`;
  }
  if (resolvedPProps.firstLineIndent) {
    attrs.textIndent = `${resolvedPProps.firstLineIndent}pt`;
  }
  if (resolvedPProps.lineSpacing && resolvedPProps.lineSpacing !== 1) {
    attrs.lineHeight = String(resolvedPProps.lineSpacing);
  }

  // Section break — flag for resolvePageBreaks to handle
  if (pPrEl && getChildNS(pPrEl, W_NS, 'sectPr')) {
    attrs._hasSectBreak = true;
  }

  // List numbering — store as internal attrs for listParser to consume
  // These are prefixed with _ and stripped before final output
  if (resolvedPProps.numId && resolvedPProps.numId !== '0') {
    attrs._numId = resolvedPProps.numId;
    attrs._numLevel = resolvedPProps.numLevel ?? 0;
  } else if (resolvedPProps.styleId) {
    // Check for style-linked numbering
    const styleNumId = ctx.styleNumIdMap.get(resolvedPProps.styleId);
    if (styleNumId && styleNumId !== '0') {
      attrs._numId = styleNumId;
      attrs._numLevel = resolvedPProps.numLevel ?? 0;
    }
  }

  const node: PMNode = { type: nodeType, attrs };

  // ProseMirror requires at least one child for non-empty paragraphs
  if (content.length > 0) {
    node.content = content;
  }

  return node;
}

/**
 * Parse the inline content of a <w:p> — runs, hyperlinks, comments, track changes.
 * Returns an array of ProseMirror inline nodes (text, hardBreak, etc.)
 *
 * Comment ranges and track changes are handled here because they are
 * sibling elements of <w:r> within <w:p>:
 *   - <w:commentRangeStart> / <w:commentRangeEnd> toggle active comment state
 *   - <w:ins> wraps runs that were inserted (tracked)
 *   - <w:del> wraps runs that were deleted (tracked, with <w:delText>)
 */
function parseParagraphContent(pEl: Element, ctx: ParseContext): PMNode[] {
  const nodes: PMNode[] = [];
  const children = pEl.childNodes;
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;
  const trackChangeTracker = ctx.trackChangeTracker as
    | TrackChangeTracker
    | undefined;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    switch (localName) {
      case 'r': {
        // Regular run — parse and apply active comment marks
        const runNodes = parseRun(el, ctx);
        applyCommentMarks(runNodes, commentTracker);
        nodes.push(...runNodes);
        break;
      }

      case 'hyperlink': {
        // Hyperlink wrapping runs
        const linkNodes = parseHyperlink(el, ctx);
        applyCommentMarks(linkNodes, commentTracker);
        nodes.push(...linkNodes);
        break;
      }

      // --- Comment range markers ---
      case 'commentRangeStart': {
        const id =
          el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
        if (id && commentTracker) {
          commentTracker.handleCommentRangeStart(id);
        }
        break;
      }

      case 'commentRangeEnd': {
        const id =
          el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
        if (id && commentTracker) {
          commentTracker.handleCommentRangeEnd(id);
        }
        break;
      }

      // --- Track changes ---
      case 'ins': {
        if (trackChangeTracker) {
          const insNodes = parseInsertion(el, ctx, trackChangeTracker);
          applyCommentMarks(insNodes, commentTracker);
          nodes.push(...insNodes);
        } else {
          // No tracker — parse child runs normally (ignore track change metadata)
          const fallbackNodes = parseInsertionFallback(el, ctx);
          nodes.push(...fallbackNodes);
        }
        break;
      }

      case 'del': {
        if (trackChangeTracker) {
          const delNodes = parseDeletion(el, ctx, trackChangeTracker);
          applyCommentMarks(delNodes, commentTracker);
          nodes.push(...delNodes);
        }
        // If no tracker, deleted text is omitted (matches Word's default view)
        break;
      }

      // Tracked MOVES. w:moveTo is the destination (text now lives here →
      // insertion-style); w:moveFrom is the source (text moved away →
      // deletion-style, runs use <w:delText>). Without these cases both fell
      // through to `default` and the moved text vanished on import. parseInsertion/
      // parseDeletion iterate child runs, so they work on moveTo/moveFrom too.
      case 'moveTo': {
        if (trackChangeTracker) {
          const moveNodes = parseInsertion(el, ctx, trackChangeTracker);
          applyCommentMarks(moveNodes, commentTracker);
          nodes.push(...moveNodes);
        } else {
          const fallbackNodes = parseInsertionFallback(el, ctx);
          nodes.push(...fallbackNodes);
        }
        break;
      }

      case 'moveFrom': {
        if (trackChangeTracker) {
          const moveNodes = parseDeletion(el, ctx, trackChangeTracker);
          applyCommentMarks(moveNodes, commentTracker);
          nodes.push(...moveNodes);
        }
        // No tracker — moved-from text omitted (it lives at the destination).
        break;
      }

      // --- Math formulas (OMML) ---
      case 'oMath': {
        // Inline math: <m:oMath> → inlineMath node
        const latex = convertOmmlToLatex(el);
        if (latex) {
          nodes.push({
            type: 'inlineMath',
            attrs: { latex },
          });
        }
        break;
      }

      case 'oMathPara': {
        // Display math: <m:oMathPara> → _mathBlock placeholder
        // Will be split out of the paragraph in index.ts (like images)
        const latex = convertOmmlToLatex(el);
        if (latex) {
          nodes.push({
            type: '_mathBlock',
            attrs: { latex },
          });
        }
        break;
      }

      // Inline Structured Document Tags — unwrap and parse inner runs
      case 'sdt': {
        const sdtContentEl = findSdtContent(el);
        if (sdtContentEl) {
          const innerNodes = parseParagraphContent(sdtContentEl, ctx);
          nodes.push(...innerNodes);
        }
        break;
      }

      // Skip known non-content elements
      case 'pPr': // already handled
      case 'bookmarkStart':
      case 'bookmarkEnd':
      case 'proofErr':
      case 'commentRangeStart': // handled above (redundant case for clarity)
      case 'commentRangeEnd':
        break;

      default:
        break;
    }
  }

  return nodes;
}

/**
 * Apply active comment marks to all text nodes in an array.
 * Also records text content for comment metadata.
 */
function applyCommentMarks(
  nodes: PMNode[],
  commentTracker: CommentTracker | undefined
): void {
  if (!commentTracker) return;

  const commentMarks = commentTracker.getActiveCommentMarks();
  if (commentMarks.length === 0) return;

  for (const node of nodes) {
    if (node.type === 'text') {
      node.marks = [...(node.marks || []), ...commentMarks];
      if (node.text) commentTracker.recordText(node.text);
    }
  }
}

/**
 * Fallback: parse <w:ins> child runs without track change marks.
 * Used when no TrackChangeTracker is provided — just extract the text.
 */
function parseInsertionFallback(
  insEl: Element,
  ctx: ParseContext
): PMNode[] {
  const nodes: PMNode[] = [];
  const children = insEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');
    if (localName === 'r') {
      nodes.push(...parseRun(el, ctx));
    }
  }
  return nodes;
}

/**
 * Parse a <w:hyperlink> element into text nodes with link marks.
 */
function parseHyperlink(hyperlinkEl: Element, ctx: ParseContext): PMNode[] {
  // Resolve the href from relationship ID
  const rId =
    hyperlinkEl.getAttributeNS(R_NS, 'id') ||
    hyperlinkEl.getAttribute('r:id') ||
    '';
  let href = '';

  if (rId) {
    const rel = ctx.relationships.get(rId);
    if (rel) {
      href = rel.target;
    }
  }

  // Also check for anchor (internal bookmark link)
  const anchor =
    hyperlinkEl.getAttributeNS(W_NS, 'anchor') ||
    hyperlinkEl.getAttribute('w:anchor') ||
    '';
  if (!href && anchor) {
    href = `#${anchor}`;
  }

  // Parse child runs
  const nodes: PMNode[] = [];
  const children = hyperlinkEl.childNodes;
  const commentTracker = ctx.commentTracker as CommentTracker | undefined;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    if (localName === 'r') {
      const runNodes = parseRun(el, ctx);
      // Add link mark to each text node
      if (href) {
        const linkMark: PMMark = {
          type: 'link',
          attrs: { href, target: '_blank' },
        };
        for (const node of runNodes) {
          if (node.type === 'text') {
            node.marks = [...(node.marks || []), linkMark];
          }
        }
      }
      nodes.push(...runNodes);
    } else if (localName === 'commentRangeStart') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeStart(id);
    } else if (localName === 'commentRangeEnd') {
      const id = el.getAttributeNS(W_NS, 'id') || el.getAttribute('w:id') || '';
      if (id && commentTracker) commentTracker.handleCommentRangeEnd(id);
    }
  }

  return nodes;
}

/**
 * Check if a paragraph is empty (no text content, just structural).
 */
export function isEmptyParagraph(node: PMNode): boolean {
  if (!node.content || node.content.length === 0) return true;
  return node.content.every(
    (child) =>
      child.type === 'text' && (!child.text || child.text.trim() === '')
  );
}

/**
 * Strip textStyle mark attrs that duplicate what the paragraph style already defines.
 *
 * In OOXML, Word sometimes writes explicit run formatting that matches the style
 * default (e.g., `<w:sz w:val="32"/>` on a run inside a paragraph whose style also
 * defines sz=32). Our parser emits a `textStyle` mark with `fontSize: 16pt`,
 * which then overrides TipTap's CSS. This function removes those redundant attrs.
 *
 * For headings, fontSize is NEVER stripped — our CSS heading sizes (2em, 1.5em, etc.)
 * don't match Word's defaults (16pt, 13pt, etc.), so the explicit DOCX font size
 * must be preserved to render headings at the correct size.
 */
function stripRedundantStyleMarks(
  nodes: PMNode[],
  styleRunProps: RunProperties,
  isHeading: boolean = false
): void {
  // For headings, never strip fontSize — our CSS heading sizes don't match
  // Word's defaults, so we need the explicit DOCX font size to override them.
  const styleFontSize =
    !isHeading && styleRunProps.fontSize
      ? `${styleRunProps.fontSize}pt`
      : null;
  // For headings, never strip color — heading styles often define theme colors
  // that must be preserved (e.g., green Heading2, blue Heading3).
  const styleColor = !isHeading && styleRunProps.color
    ? `#${styleRunProps.color}`
    : null;
  // Only strip fontFamily if it matches our CSS default ("Times New Roman").
  // If the style defines e.g. "Calibri" and the run also says "Calibri", we must
  // keep it — otherwise the text falls back to our CSS default Times New Roman.
  const CSS_DEFAULT_FONT = 'Times New Roman';
  const styleFontFamily =
    styleRunProps.fontFamily === CSS_DEFAULT_FONT
      ? styleRunProps.fontFamily
      : null;

  // Nothing to strip if style defines no run properties
  if (!styleFontSize && !styleColor && !styleFontFamily) return;

  for (const node of nodes) {
    if (node.type !== 'text' || !node.marks) continue;

    for (let i = node.marks.length - 1; i >= 0; i--) {
      const mark = node.marks[i];
      if (mark.type !== 'textStyle' || !mark.attrs) continue;

      const attrs = mark.attrs as Record<string, unknown>;
      // Remove attrs that match the style default
      if (styleFontSize && attrs.fontSize === styleFontSize) {
        delete attrs.fontSize;
      }
      if (styleColor && attrs.color === styleColor) {
        delete attrs.color;
      }
      if (styleFontFamily && attrs.fontFamily === styleFontFamily) {
        delete attrs.fontFamily;
      }

      // If textStyle mark has no remaining attrs, remove it entirely
      if (Object.keys(attrs).length === 0) {
        node.marks.splice(i, 1);
      }
    }

    // Clean up empty marks array
    if (node.marks.length === 0) {
      delete node.marks;
    }
  }
}

/**
 * Detect if a paragraph represents a horizontal rule.
 * In OOXML, horizontal lines are empty paragraphs with <w:pBdr><w:bottom> border.
 */
function isHorizontalRule(pPrEl: Element, pEl: Element): boolean {
  // Check for <w:pBdr> with a bottom border
  const pBdr = getChildNS(pPrEl, W_NS, 'pBdr');
  if (!pBdr) return false;

  const bottom = getChildNS(pBdr, W_NS, 'bottom');
  if (!bottom) return false;

  // Check border style is visible (not "none" or "nil")
  const val = bottom.getAttributeNS(W_NS, 'val') || bottom.getAttribute('w:val') || '';
  if (!val || val === 'none' || val === 'nil') return false;

  // Verify no <w:t> or <w:delText> elements exist (no visible text)
  const textEls = pEl.getElementsByTagNameNS(W_NS, 't');
  const delTextEls = pEl.getElementsByTagNameNS(W_NS, 'delText');
  const hasText = Array.from(textEls).some(t => (t.textContent || '').trim().length > 0)
    || Array.from(delTextEls).some(t => (t.textContent || '').trim().length > 0);

  return !hasText;
}
